/**
 * Python Results Parser
 * Parses JSON results from MangoGrid power flow execution
 */

// ============================================================
// TYPES
// ============================================================

/** Bus result from power flow */
export interface BusResult {
  /** Voltage magnitude in per-unit */
  v_mag_pu: number;
  /** Voltage angle in degrees */
  v_ang?: number;
}

/** Line result from power flow */
export interface LineResult {
  /** Active power at bus 0 (MW) */
  p0: number;
  /** Active power at bus 1 (MW) */
  p1?: number;
  /** Loading percentage */
  loading: number;
}

/** Generator result from power flow */
export interface GeneratorResult {
  /** Active power output (MW) */
  p: number;
  /** Reactive power output (MVAr) */
  q?: number;
}

/** Storage result from power flow */
export interface StorageResult {
  /** Active power (MW, positive=discharge) */
  p: number;
  /** State of charge */
  state_of_charge?: number;
}

/** Summary statistics from power flow */
export interface PowerFlowSummary {
  /** Minimum voltage (p.u.) */
  min_v: number;
  /** Maximum voltage (p.u.) */
  max_v: number;
  /** Maximum line loading (%) */
  max_load: number;
  /** Total generation (MW) */
  total_gen?: number;
  /** Total load (MW) */
  total_load?: number;
}

/** Complete power flow results */
export interface PowerFlowResults {
  /** Whether power flow converged */
  success: boolean;
  /** Error message if failed */
  error: string | null;
  /** Bus results by name */
  buses: Record<string, BusResult>;
  /** Line results by ID */
  lines: Record<string, LineResult>;
  /** Generator results by name */
  generators?: Record<string, GeneratorResult>;
  /** Storage results by name */
  storage?: Record<string, StorageResult>;
  /** Summary statistics */
  summary: PowerFlowSummary;
}

/** Raw JSON from Python */
export interface RawPowerFlowJSON {
  success: boolean;
  error: string | null;
  buses: Record<string, { v_mag_pu: number; v_ang?: number }>;
  lines: Record<string, { p0?: number; p1?: number; loading: number }>;
  generators?: Record<string, { p: number; q?: number }>;
  storage?: Record<string, { p: number; state_of_charge?: number }>;
  summary: {
    min_v?: number;
    max_v?: number;
    max_load?: number;
    total_gen?: number;
    total_load?: number;
  };
}

// ============================================================
// PARSING FUNCTIONS
// ============================================================

/**
 * Parse power flow results from JSON string
 */
export function parseResultsJSON(jsonString: string): PowerFlowResults {
  const raw = JSON.parse(jsonString) as RawPowerFlowJSON;
  return parseRawResults(raw);
}

/**
 * Parse raw results object
 */
export function parseRawResults(raw: RawPowerFlowJSON): PowerFlowResults {
  const results: PowerFlowResults = {
    success: raw.success,
    error: raw.error,
    buses: {},
    lines: {},
    summary: {
      min_v: raw.summary.min_v ?? 1.0,
      max_v: raw.summary.max_v ?? 1.0,
      max_load: raw.summary.max_load ?? 0,
      total_gen: raw.summary.total_gen,
      total_load: raw.summary.total_load,
    },
  };

  // Parse bus results
  if (raw.buses) {
    Object.entries(raw.buses).forEach(([name, data]) => {
      results.buses[name] = {
        v_mag_pu: data.v_mag_pu ?? 1.0,
        v_ang: data.v_ang ?? 0,
      };
    });
  }

  // Parse line results
  if (raw.lines) {
    Object.entries(raw.lines).forEach(([id, data]) => {
      results.lines[id] = {
        p0: data.p0 ?? 0,
        p1: data.p1,
        loading: data.loading ?? 0,
      };
    });
  }

  // Parse generator results
  if (raw.generators) {
    results.generators = {};
    Object.entries(raw.generators).forEach(([name, data]) => {
      results.generators![name] = {
        p: data.p ?? 0,
        q: data.q,
      };
    });
  }

  // Parse storage results
  if (raw.storage) {
    results.storage = {};
    Object.entries(raw.storage).forEach(([name, data]) => {
      results.storage![name] = {
        p: data.p ?? 0,
        state_of_charge: data.state_of_charge,
      };
    });
  }

  return results;
}

/**
 * Validate results structure
 */
export function validateResults(results: unknown): results is PowerFlowResults {
  if (typeof results !== 'object' || results === null) {
    return false;
  }

  const r = results as Record<string, unknown>;

  if (typeof r.success !== 'boolean') return false;
  if (typeof r.buses !== 'object') return false;
  if (typeof r.lines !== 'object') return false;
  if (typeof r.summary !== 'object') return false;

  return true;
}

// ============================================================
// ANALYSIS FUNCTIONS
// ============================================================

/**
 * Check if any buses have voltage violations
 */
export function hasVoltageViolations(
  results: PowerFlowResults,
  minV: number = 0.95,
  maxV: number = 1.05
): boolean {
  return Object.values(results.buses).some(
    (bus) => bus.v_mag_pu < minV || bus.v_mag_pu > maxV
  );
}

/**
 * Get buses with voltage violations
 */
export function getVoltageViolations(
  results: PowerFlowResults,
  minV: number = 0.95,
  maxV: number = 1.05
): { undervoltage: string[]; overvoltage: string[] } {
  const undervoltage: string[] = [];
  const overvoltage: string[] = [];

  Object.entries(results.buses).forEach(([name, bus]) => {
    if (bus.v_mag_pu < minV) {
      undervoltage.push(name);
    } else if (bus.v_mag_pu > maxV) {
      overvoltage.push(name);
    }
  });

  return { undervoltage, overvoltage };
}

/**
 * Check if any lines are overloaded
 */
export function hasOverloadedLines(
  results: PowerFlowResults,
  maxLoading: number = 100
): boolean {
  return Object.values(results.lines).some((line) => line.loading > maxLoading);
}

/**
 * Get overloaded lines
 */
export function getOverloadedLines(
  results: PowerFlowResults,
  maxLoading: number = 100
): string[] {
  return Object.entries(results.lines)
    .filter(([, line]) => line.loading > maxLoading)
    .map(([id]) => id);
}

/**
 * Calculate total losses from line results
 */
export function calculateTotalLosses(results: PowerFlowResults): number {
  let losses = 0;

  Object.values(results.lines).forEach((line) => {
    if (line.p0 !== undefined && line.p1 !== undefined) {
      losses += Math.abs(line.p0 + line.p1);
    }
  });

  return losses;
}

/**
 * Get a human-readable summary of results
 */
export function getResultsSummary(results: PowerFlowResults): string[] {
  const messages: string[] = [];

  if (!results.success) {
    messages.push(`❌ Power flow failed: ${results.error}`);
    return messages;
  }

  messages.push('✅ Power flow converged');

  // Voltage summary
  const { min_v, max_v } = results.summary;
  messages.push(`Voltage range: ${min_v.toFixed(4)} - ${max_v.toFixed(4)} p.u.`);

  // Check voltage violations
  const violations = getVoltageViolations(results);
  if (violations.undervoltage.length > 0) {
    messages.push(`⚠️ Undervoltage at: ${violations.undervoltage.join(', ')}`);
  }
  if (violations.overvoltage.length > 0) {
    messages.push(`⚠️ Overvoltage at: ${violations.overvoltage.join(', ')}`);
  }

  // Line loading
  messages.push(`Max line loading: ${results.summary.max_load.toFixed(1)}%`);

  const overloaded = getOverloadedLines(results);
  if (overloaded.length > 0) {
    messages.push(`⚠️ Overloaded lines: ${overloaded.join(', ')}`);
  }

  // Generation/load balance
  if (results.summary.total_gen !== undefined && results.summary.total_load !== undefined) {
    messages.push(`Total generation: ${results.summary.total_gen.toFixed(2)} MW`);
    messages.push(`Total load: ${results.summary.total_load.toFixed(2)} MW`);
  }

  return messages;
}

export default parseResultsJSON;

